import * as React from 'react';
import { useAtomValue } from 'jotai';
import { Table } from 'antd';
import { useCriticalEvents, useDropoffQuery } from '@src/services/queries';
import * as store from '../../store';
import { usePatternQueryPayload, checkPropagationStepDropoffAvailable } from '../../utils';
import { IndexCell, MonospaceCell, PatternArrow, PatternContainer, PatternEvent } from './styled';

const DropoffReasonTable = () => {
  const { data: criticalEvents } = useCriticalEvents();
  const [{ limit: _limit, ...payload }, { pattern }] = usePatternQueryPayload();
  const dropoffIndex = useAtomValue(store.dropOffIndexAtom);
  const { data, isFetching } = useDropoffQuery(
    {
      ...payload,
      dropoff_index: dropoffIndex,
    },
    {
      enabled: checkPropagationStepDropoffAvailable(dropoffIndex, payload.filters),
    },
  );

  const prefixMilestones = React.useMemo(
    () =>
      pattern
        .slice(0, dropoffIndex)
        .map((it) => criticalEvents?.criticalEventMap.get(it)?.name ?? it),
    // Only re-compute when data changes to avoid table re-rendering during loading state
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, criticalEvents?.criticalEventMap],
  );

  const columns = React.useMemo<GetProp<typeof Table<NonNullable<typeof data>[0]>, 'columns'>>(
    () => [
      {
        title: '#',
        dataIndex: 'key',
        key: 'key',
        width: 60,
        align: 'center',
        render: (value, record, index) => <IndexCell>{index + 1}</IndexCell>,
      },
      {
        title: 'Reason',
        dataIndex: 'reason',
        key: 'reason',
        width: 200,
        render: (value: string) => <MonospaceCell>{value}</MonospaceCell>,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        width: 300,
        render: (value: string) => <MonospaceCell>{value}</MonospaceCell>,
      },
      {
        title: 'Pattern',
        dataIndex: 'pattern',
        key: 'pattern',
        width: 300,
        render: (value: string[]) => {
          const renderPattern = (patternArray: any[]) => {
            const elements: React.ReactNode[] = [];
            patternArray.forEach((item, index) => {
              if (typeof item === 'string') {
                elements.push(
                  <PatternEvent style={{ whiteSpace: 'nowrap' }} key={index}>
                    {item}
                  </PatternEvent>,
                );
              }
              if (index < patternArray.length - 1) {
                elements.push(<PatternArrow key={`arrow-${index}`}>→</PatternArrow>);
              }
            });

            return elements;
          };

          return (
            <PatternContainer>{renderPattern([...prefixMilestones, ...value])}</PatternContainer>
          );
        },
      },
    ],
    [prefixMilestones],
  );

  return (
    <Table
      loading={isFetching}
      columns={columns}
      dataSource={data ?? []}
      pagination={false}
      size="middle"
      bordered
    />
  );
};

export default React.memo(DropoffReasonTable);
